import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { getContent, updateContent } from '../../../services/api'
import type { Content } from '../../../types/database'

const springConfig = { type: "spring", stiffness: 60, damping: 10 }

export default function DashboardContent() {
  const [content, setContent] = useState<Content | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [techInput, setTechInput] = useState('')

  useEffect(() => {
    loadContent()
  }, [])

  const loadContent = async () => {
    setLoading(true)
    const data = await getContent()
    if (!data) {
      // Initialize with defaults if no content exists
      setContent({
        id: '1',
        code_example: '// React Component with Framer Motion\nconst ProjectCard = ({ title, description }) => {\n  return (\n    <motion.div\n      initial={{ opacity: 0, y: 20 }}\n      animate={{ opacity: 1, y: 0 }}\n      whileHover={{ scale: 1.05 }}\n      className="neu p-6 rounded-2xl"\n    >\n      <h3 className="text-xl font-semibold mb-2">{title}</h3>\n      <p className="text-text-primary/70">{description}</p>\n    </motion.div>\n  )\n}',
        code_language: 'typescript',
        technologies: [],
        quick_info_location: 'Available Worldwide',
        quick_info_response_time: 'Usually within 24 hours',
        quick_info_availability: 'Open to new projects',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      })
    } else {
      setContent(data)
    }
    setLoading(false)
  }

  const handleSave = async () => {
    if (!content) return
    setSaving(true)
    try {
      await updateContent(content)
      alert('Content updated successfully!')
    } catch (error) {
      console.error('Error updating content:', error)
      alert('Error updating content')
    } finally {
      setSaving(false)
    }
  }

  const updateField = (field: keyof Content, value: any) => {
    if (content) {
      setContent({ ...content, [field]: value })
    }
  }

  const addTechnology = () => {
    if (techInput.trim() && content) {
      const technologies = [...(content.technologies || []), techInput.trim()]
      setContent({ ...content, technologies })
      setTechInput('')
    }
  }

  const removeTechnology = (index: number) => {
    if (content) {
      const technologies = content.technologies.filter((_, i) => i !== index)
      setContent({ ...content, technologies })
    }
  }

  if (loading) {
    return <div className="text-text-primary/70">Loading content...</div>
  }

  if (!content) {
    return <div className="text-text-primary/70">No content found</div>
  }

  return (
    <div>
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6 sm:mb-8">
        <h2 className="text-2xl sm:text-3xl font-semibold text-text-primary">Content Management</h2>
        <motion.button
          onClick={handleSave}
          disabled={saving}
          className="px-5 sm:px-6 py-2.5 sm:py-3 neu rounded-xl font-medium text-text-primary neu-hover touch-manipulation w-full sm:w-auto text-sm sm:text-base disabled:opacity-50"
          whileHover={saving ? {} : { scale: 1.02, y: -1 }}
          whileTap={saving ? {} : { scale: 0.98 }}
          transition={springConfig}
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </motion.button>
      </div>

      <div className="space-y-6 sm:space-y-8">
        {/* Code Example */}
        <div className="neu rounded-2xl p-4 sm:p-6">
          <h3 className="text-xl font-semibold mb-4 text-text-primary">Code Example</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-xs sm:text-sm font-medium mb-2 text-text-primary/70 uppercase tracking-wide">
                Language
              </label>
              <input
                type="text"
                value={content.code_language || ''}
                onChange={(e) => updateField('code_language', e.target.value)}
                className="w-full px-4 sm:px-5 py-3.5 sm:py-4 neu rounded-2xl text-text-primary placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-accent/30 transition-all font-normal text-base touch-manipulation"
                placeholder="typescript"
              />
            </div>
            <div>
              <label className="block text-xs sm:text-sm font-medium mb-2 text-text-primary/70 uppercase tracking-wide">
                Code
              </label>
              <textarea
                value={content.code_example || ''}
                onChange={(e) => updateField('code_example', e.target.value)}
                rows={12}
                className="w-full px-4 sm:px-5 py-3.5 sm:py-4 neu rounded-2xl text-text-primary placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-accent/30 transition-all font-mono text-sm touch-manipulation resize-y"
                placeholder="// Your code example here"
              />
            </div>
          </div>
        </div>

        {/* Technologies */}
        <div className="neu rounded-2xl p-4 sm:p-6">
          <h3 className="text-xl font-semibold mb-4 text-text-primary">Technologies</h3>
          <div className="space-y-4">
            <div className="flex gap-2">
              <input
                type="text"
                value={techInput}
                onChange={(e) => setTechInput(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    addTechnology()
                  }
                }}
                className="flex-1 px-4 sm:px-5 py-3.5 sm:py-4 neu rounded-2xl text-text-primary placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-accent/30 transition-all font-normal text-base touch-manipulation"
                placeholder="Add a technology"
              />
              <motion.button
                onClick={addTechnology}
                className="px-4 py-3.5 sm:py-4 neu rounded-xl text-accent hover:text-accent-secondary transition-colors neu-hover touch-manipulation"
                whileHover={{ scale: 1.02, y: -1 }}
                whileTap={{ scale: 0.98 }}
                transition={springConfig}
              >
                Add
              </motion.button>
            </div>
            <div className="flex flex-wrap gap-2">
              {(content.technologies || []).map((tech, index) => (
                <span
                  key={index}
                  className="inline-flex items-center gap-2 px-3 py-1.5 neu rounded-full text-sm text-accent"
                >
                  {tech}
                  <button
                    onClick={() => removeTechnology(index)}
                    className="hover:text-red-400 transition-colors touch-manipulation"
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* Quick Info */}
        <div className="neu rounded-2xl p-4 sm:p-6">
          <h3 className="text-xl font-semibold mb-4 text-text-primary">Quick Info</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-xs sm:text-sm font-medium mb-2 text-text-primary/70 uppercase tracking-wide">
                Location
              </label>
              <input
                type="text"
                value={content.quick_info_location || ''}
                onChange={(e) => updateField('quick_info_location', e.target.value)}
                className="w-full px-4 sm:px-5 py-3.5 sm:py-4 neu rounded-2xl text-text-primary placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-accent/30 transition-all font-normal text-base touch-manipulation"
                placeholder="Available Worldwide"
              />
            </div>
            <div>
              <label className="block text-xs sm:text-sm font-medium mb-2 text-text-primary/70 uppercase tracking-wide">
                Response Time
              </label>
              <input
                type="text"
                value={content.quick_info_response_time || ''}
                onChange={(e) => updateField('quick_info_response_time', e.target.value)}
                className="w-full px-4 sm:px-5 py-3.5 sm:py-4 neu rounded-2xl text-text-primary placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-accent/30 transition-all font-normal text-base touch-manipulation"
                placeholder="Usually within 24 hours"
              />
            </div>
            <div>
              <label className="block text-xs sm:text-sm font-medium mb-2 text-text-primary/70 uppercase tracking-wide">
                Availability
              </label>
              <input
                type="text"
                value={content.quick_info_availability || ''}
                onChange={(e) => updateField('quick_info_availability', e.target.value)}
                className="w-full px-4 sm:px-5 py-3.5 sm:py-4 neu rounded-2xl text-text-primary placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-accent/30 transition-all font-normal text-base touch-manipulation"
                placeholder="Open to new projects"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

